import {onBlockLoaded} from "@plaudit/frontend-apis";

import {buildVariationEvent, findEventDataForProductId, fireViewItem} from "./view-item-common";

/**
 * Follow-up view_item events for the block "Add to Cart + Options" block (woocommerce/add-to-cart-with-options),
 * which drives variation selection through the Interactivity API rather than the classic jQuery form.
 *
 * The initial view_item is still fired by the classic generator (its JSON tag is server-rendered on every
 * single-product page); this module only handles variation changes for the block form.
 *
 * It also, due to lack of proper support on WooCommerce's part, has to use some pretty hacky workarounds.
 */

interface VariationInfo {
	item_variant?: string;
	price?: number;
}

const variationMaps = new Map<string, Record<string, VariationInfo>>();

const lastVariationId = new Map<string, string>();

function getProductOrVariationIdFromForm(blockRoot: Element, form?: HTMLFormElement|null) {
	if (form) {
		const idElement = form.elements.namedItem("variation_id") ?? form.elements.namedItem("product_id") ?? form.elements.namedItem("add-to-cart");
		return idElement instanceof HTMLInputElement ? idElement.value : null;
	}
	return blockRoot.getAttribute("data-product-id");
}

function fireChange(blockRoot: Element, form: HTMLFormElement, productId: string) {
	const detectedId = getProductOrVariationIdFromForm(blockRoot, form);
	const base = findEventDataForProductId(productId);
	if (!!window.gtag && base && detectedId && detectedId !== productId && detectedId !== lastVariationId.get(productId)) {
		// We only fire if the variation has changed AND the selected item is not just the root product
		lastVariationId.set(productId, detectedId);

		const {item_variant, price} = (variationMaps.get(productId) ?? {})[detectedId] ?? {};
		fireViewItem(buildVariationEvent(base, item_variant || detectedId, price), "select_item");
	}
}

/**
 * Reads every server-rendered { [variation_id]: { item_variant, price } } map on the page, keyed by product
 * id (data-product-id). Each Beta block emits its own map, so a page with several variable products yields
 * one entry per product.
 */
onBlockLoaded({
	blockOrClassName: "plaudit-woocommerce-ga-integration__view_item__variations",
	onAdd(blockRoot) {
		const productId = blockRoot.getAttribute("data-product-id");
		if (!productId || !blockRoot.textContent) {
			return;
		}
		try {
			const map = JSON.parse(blockRoot.textContent);
			if (typeof map === "object" && map !== null) {
				variationMaps.set(productId, map);
			}
		} catch {
			// Skip a malformed tag rather than losing the others.
		}

		const form = blockRoot.closest("form");
		if (!form) {
			return;
		}

		form.addEventListener('change', e => {
			if ((e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) && e.target.id.startsWith("wc_product_attribute_") && e.target.value === "") {
				// We don't fire a select event here because state means that the user has deselected the variation
				lastVariationId.delete(productId);
			} else {
				fireChange(blockRoot, form, productId);
			}
		});
		new MutationObserver(changes => {
			if (changes.find(change => change.target instanceof HTMLInputElement && (change.target.name === 'product_id' || change.target.name === 'variation_id'))) {
				fireChange(blockRoot, form, productId);
			}
		}).observe(form, {attributes: true, subtree: true});
	}
});
